/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useState } from "react";
import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import { mediaReconciliationService } from "../../../../service/mediaReconciliationServices";
import {
	ReconPowderBatchDetails,
	ReconPowderListBatchResponse,
} from "./reconcilePowder.types";
import MediaPreparationTable from "../../../common/commonComponents/mediaPreparationTable";
import { getMediaReconciliationColumns } from "./partials/columns";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/constants";
import Pagination from "../../../../widgets/pagination";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReconSearchFilter from "../common/ReconSearchFilter";
import ReconStatCard from "../common/ReconStatsCard";
import ReconTitleHeader from "../common/ReconTitleHeader";
import { userService } from "../../../../service/userService";

const ReconcileMediaPowder: React.FC = () => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [PowderBatchDetails, setPowderBatchDetails] = useState<
		ReconPowderBatchDetails[]
	>([]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [totalTableElements, setTotalTableElements] = useState<number>(0);
	const [stats, setStats] = useState({
		pendingReconciliation: 0,
		overdueReconciliation: 0,
		lastAuditDate: "-",
	});

	const [statusFilter, setStatusFilter] = useState("");
	const [loading, setLoading] = useState(false);
	const [permission, setPermission] = useState<any>([]);
	const getPermissionByRole = async (role: number) => {
		try {
		const response = await userService.getPermissionByRole(role);

		if (response.status && response.statusCode === 200) {
			setPermission(response.data.permissions);
		}
		} catch (error) {
		console.error(error);
		}
	};

	const [sort, setSort] = useState("id");
	  const [order, setOrder] = useState("desc");
	  const mapStatusToApi = (status: string) => {
		if (status === "Expired") return "expired";
		return status;
	  };
	
	  const handleSort = (key: string, value: boolean) => {
		setSort(key);
		setOrder(value ? "asc" : "desc");
		setCurrentPage(1);
	  };


	useEffect(() => {
		const storedData = localStorage.getItem("userDetails");

		if (storedData) {
		const storedObject = JSON.parse(storedData);
		getPermissionByRole(storedObject[0].role_master_id);
		}
	}, []);

	const navigate = useNavigate();
	const { t } = useTranslation();

	const getPowderBatchDetails = async (): Promise<void> => {
		setLoading(true);
		try {
			const queryParams = {
				page: currentPage.toString(),
				size: pageSize.toString(),
				sort,
				order,
				keyword: searchTerm,
				reconciliation_status: statusFilter,
			};
			const response: ReconPowderListBatchResponse =

				await mediaReconciliationService.getReconPowderBatchList(
					queryParams,
				);

			if (response && response.statusCode === 200) {
				setPowderBatchDetails(response.data);
				setTotalPages(response.totalPages);
				setCurrentPage(Number(response.page));
				setTotalTableElements(response.totalElements);

				setStats({
					pendingReconciliation: response.pending_reconciliation,
					overdueReconciliation: response.overdue_reconciliation,
					lastAuditDate: response.last_audit_date,
				});
			}
		} catch (err) {
			alert(err)
		}
		finally {
			setLoading(false);
		}
	};

	const handlePageChange = (page: number, size?: number) => {
		setCurrentPage(page);

		if (size) {
			setPageSize(size);
		}
	};

	useEffect(() => {
		getPowderBatchDetails();
	}, [currentPage, pageSize, searchTerm, statusFilter, sort, order]);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaReconcile.mediaPowder.title")} pageDescription={t('pageDescription.mediaPowderReconciliation.description')} />
			<div className="p-8 max-w-full animate-in fade-in duration-500 min-h-full bg-[#F8FAFC]">

				{/* <ReconTitleHeader title={t("mediaReconcile.mediaPowder.title")} infoText={t("mediaReconcile.mediaPowder.infoText")} /> */}

				<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-3">
					<ReconStatCard
						label={t(
							"mediaReconcile.mediaPowder.stats.pendingReconciliation",
						)}
						value={stats.pendingReconciliation}
						sublabel={t(
							"mediaReconcile.mediaPowder.stats.lotsRequiringVerification",
						)}
						variant="light"
						accent="yellow"
					/>

					<ReconStatCard
						label={t(
							"mediaReconcile.mediaPowder.stats.overdueReconciliation",
						)}
						value={stats.overdueReconciliation}
						sublabel={t(
							"mediaReconcile.mediaPowder.stats.immediateActionRequired",
						)}
						variant="light"
						accent="red"
					/>

					<ReconStatCard
						label={t("mediaReconcile.mediaPowder.stats.lastAuditDate")}
						value={stats.lastAuditDate}
						sublabel={t(
							"mediaReconcile.mediaPowder.stats.systemWideVerification",
						)}
						variant="dark"
					/>
				</div>

				<div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
					<div className="px-6 py-2 bg-slate-50/30 border-b border-gray-200 flex items-center">
						<div className="flex items-center gap-1 text-sm text-gray-700">
							<span className="font-semibold">
								{t("mediaReconcile.title")}
							</span>
							<span className="text-gray-400 font-light">|</span>
							<span className="text-gray-500">{totalTableElements}</span>
						</div>
						<ReconSearchFilter
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							searchPlaceholder={t(
								"mediaReconcile.mediaPowder.searchPlaceHolder",
							)}
							statusFilter={statusFilter}
							setStatusFilter={(value) => {
								setStatusFilter(value);
								setCurrentPage(1);
							}}
						/>
					</div>

					<MediaPreparationTable
						columns={getMediaReconciliationColumns(t)}
						data={PowderBatchDetails}
						currentPage={currentPage}
						loading={loading}
						pageSize={pageSize}
						onSortClick={handleSort}
						emptyMessage={t("mediaReconcile.noDataToDisplay")}
						renderAction={(row) => (
						  	permission?.includes("start_media_powder_reconciliation") ? (
							  <button
								onClick={() =>
									navigate(
										`/startMediaReconciliation/${row.batch_id}/powder/${row.reconciliation_status}`,
									)
								}
								className="px-6 py-2 bg-[#FF7043] text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-sm border-none"
							  >
								{t("mediaReconcile.mediaPowder.startReconcile")}
							  </button>
						  	) : null
						)}
					/>
				</div>
			</div>
			<Pagination
				page={currentPage}
				totalPages={totalPages}
				handlePageChange={handlePageChange}
			/>
		</div>
	);
};

export default ReconcileMediaPowder;
